import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Stack,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
} from '@mui/material';
import React from 'react';
import { poppins300 } from 'theme/common.style';

const BannerMoreInfoDialog = ({ open, onClose, userName, status }) => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));

  const DialogRenderer = React.memo(({ type, userName }) => {
    switch (type) {
      case 'COMPLETE_BUYER_PROFILE':
        return <CompleteBuyerProfile />;

      case 'PREPARING_CONTRACT':
        return <PreparingContract userName={userName} />;

      case 'CAMPAIGN_PAUSED':
        return <CampaignPaused />;

      case 'STAGE_2_STARTED':
        return <Stage2Started />;

      case 'CAMPAIGN_ENDED':
        return <CampaignEnded />;

      case 'WINNER_NOMINATED':
        return <WinnerNominated />;

      default:
        return null;
    }
  });

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogContent>
        <DialogRenderer type={status} userName={userName} />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            width: '126px',
            height: matchessm ? '35px' : '42px',
            borderRadius: '5px',
            ...poppins300,
            fontSize: '14px',
            textTransform: 'none',
            border: '1px solid #E6F2FF',
            backgroundColor: 'rgb(33, 132, 253)',
            color: '#E6F2FF',
            '&:hover': {
              bgcolor: 'rgb(33, 132, 253)',
              color: '#E6F2FF',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CompleteBuyerProfile = () => {
  return (
    <Stack spacing={3}>
      <Typography
        color="black"
        sx={{
          fontFamily: 'poppins',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
        Get Started with retreX!
      </Typography>

      <Typography
        sx={{
          fontFamily: 'poppins',
          fontWeight: 600,
          fontSize: '24px',
        }}
      >
        Complete Your Buyer Profile
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        To start exploring properties and submitting offers on retreX, you'll
        need to complete your Buyer Profile.
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        Your profile helps your agent match you with the right properties and
        ensures you're ready to act when the time comes.
      </Typography>

      <Box>
        <Typography fontWeight={600} sx={{ fontFamily: 'poppins' }}>
          Here's what you'll need to complete
        </Typography>

        <Stepper orientation="vertical">
          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Personal Details
            </StepLabel>

            <StepContent>
              Your name, email address and mobile number.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Notification Preferences
            </StepLabel>

            <StepContent>Choose how you'd like to be kept updated</StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Your Signature
            </StepLabel>

            <StepContent>
              drawn or uploaded, used for e-signing documents during the
              transaction{' '}
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      <Alert severity="info">
        Once your profile is complete, you'll receive your unique Buyer ID. Keep
        this handy — your agent may ask for it to check you in to a property
        campaign.
      </Alert>

      <Alert severity="warning">
        <b>Why it matters:</b> You won't be able to submit offers or participate
        in a campaign until your profile is complete and you've been checked in
        by your agent.
      </Alert>
    </Stack>
  );
};
const PreparingContract = ({ userName }) => {
  return (
    <Stack spacing={3}>
      <Typography
        color="black"
        sx={{ fontFamily: 'poppins', fontSize: '16px', fontWeight: 500 }}
      >
        E-Sign Request — {userName} Preparing Contract of Sale for E-Sign
      </Typography>

      <Typography
        sx={{ fontFamily: 'poppins', fontWeight: 600, fontSize: '24px' }}
      >
        Your Contract of Sale Is Being Prepared
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        Congratulations — you've been selected as the winning buyer for this
        property. Your agent is currently preparing the Contract of Sale for
        electronic signing
      </Typography>

      <Box>
        <Typography fontWeight={600} sx={{ fontFamily: 'poppins' }}>
          What happens next
        </Typography>

        <Stepper orientation="vertical">
          <Step active sx={{ fontFamily: 'poppins' }}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Preparing Contract
            </StepLabel>

            <StepContent sx={{ fontFamily: 'poppins' }}>
              your agent is reviewing and finalising the contract details. You
              don't need to do anything at this stage.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Sign Pending
            </StepLabel>

            <StepContent sx={{ fontFamily: 'poppins' }}>
              once the contract is ready, you'll receive a notification to
              review and sign. Check that your email and notification settings
              are up to date.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Contract Signed
            </StepLabel>

            <StepContent sx={{ fontFamily: 'poppins' }}>
              once all parties have signed, exchange can be confirmed and the
              transaction moves forward.
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      <Alert severity="warning" sx={{ fontFamily: 'poppins' }}>
        Please be ready to sign promptly when notified. Delays in signing can
        affect the terms and conditions of your contract
      </Alert>

      <Typography variant="body2" sx={{ fontFamily: 'poppins' }}>
        If you have any questions about the contract, we recommend speaking with
        your solicitor or conveyancer before signing.
      </Typography>
    </Stack>
  );
};
const CampaignPaused = ({ userName }) => {
  return (
    <Stack spacing={3}>
      <Typography
        color="black"
        sx={{
          fontFamily: 'poppins',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
        Campaign Paused
      </Typography>

      <Typography
        sx={{
          fontFamily: 'poppins',
          fontWeight: 600,
          fontSize: '24px',
        }}
      >
        This Campaign Has Been Temporarily Paused
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        The campaign for this property has been paused by the agent. While a
        campaign is paused, no new offers can be submitted and no changes can be
        made to existing offers.
      </Typography>

      <Box>
        <Typography fontWeight={600} sx={{ fontFamily: 'poppins' }}>
          What this means for you
        </Typography>

        <Stepper orientation="vertical">
          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              No Activity During Pause
            </StepLabel>

            <StepContent>
              You will not miss anything while the campaign is paused — no
              bidding or activity can occur for any buyer during this time.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Existing Offers Remain
            </StepLabel>

            <StepContent>
              Your existing offers remain on record and will not be affected by
              the pause.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Resume Notification
            </StepLabel>

            <StepContent>
              You will be notified immediately when the campaign resumes.
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      <Alert severity="info">
        <b>What to do next: </b>If you have questions about why the campaign has
        been paused or when it will resume, please contact your agent directly
        using the button below.
      </Alert>
    </Stack>
  );
};
const Stage2Started = ({ userName }) => {
  return (
    <Stack spacing={3}>
      <Typography
        color="black"
        sx={{
          fontFamily: 'poppins',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
        Stage 2 Timer Has Begun!
      </Typography>

      <Typography
        sx={{
          fontFamily: 'poppins',
          fontWeight: 600,
          fontSize: '24px',
        }}
      >
        Stage 2 Competitive Offers Are Now Live
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        Stage 2 has started for your property. This is the competitive digital
        offer phase, where registered buyers can submit and increase their
        offers in real time.
      </Typography>

      <Box>
        <Typography fontWeight={600} sx={{ fontFamily: 'poppins' }}>
          How Stage 2 Works
        </Typography>

        <Stepper orientation="vertical">
          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Offers Can Only Increase
            </StepLabel>

            <StepContent>
              Buyers can submit offers but cannot withdraw them during Stage 2.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Minimum Increment Rules
            </StepLabel>

            <StepContent>
              All offers must meet the minimum increment set for this campaign.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Offer Visibility
            </StepLabel>

            <StepContent>
              The highest current offer is visible to you, your agent, and all
              buyers. Lower offers are only visible to the buyer who made them.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Countdown Timer
            </StepLabel>

            <StepContent>
              A countdown timer controls when Stage 2 ends. The timer may reset
              if a valid offer is submitted close to the end.
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      <Alert severity="info">
        Your agent will keep you updated as offers come in. Once the timer ends,
        your agent will nominate a winner and contact you to begin the contract
        process.
      </Alert>

      <Alert severity="warning">
        Stage 2 is a competitive private treaty sale — not a legal auction. Your
        normal cooling-off period obligations still apply after exchange unless
        a s66W certificate is provided by the buyer's solicitor.
      </Alert>

      <Typography
        variant="body2"
        sx={{
          fontFamily: 'poppins',
          color: 'text.secondary',
        }}
      >
        If you have questions, contact your agent.
      </Typography>
    </Stack>
  );
};
const CampaignEnded = ({ userName }) => {
  return (
    <Stack spacing={3}>
      <Typography
        color="black"
        sx={{
          fontFamily: 'poppins',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
        Campaign Ended! — Winner Pending
      </Typography>

      <Typography
        sx={{
          fontFamily: 'poppins',
          fontWeight: 600,
          fontSize: '24px',
        }}
      >
        Your Campaign Has Ended — Winner Being Confirmed
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        The offer period for your property has now closed. Your agent is
        reviewing the final offers and is in the process of nominating a winning
        buyer.
      </Typography>

      <Box>
        <Typography fontWeight={600} sx={{ fontFamily: 'poppins' }}>
          What Happens Next
        </Typography>

        <Stepper orientation="vertical">
          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Winning Buyer Nominated
            </StepLabel>

            <StepContent>Your agent nominates the winning buyer</StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Contract of Sale Prepared
            </StepLabel>

            <StepContent>
              Your agent prepares the Contract of Sale and issues it to both you
              and the buyer for signing.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Exchange Process Begins
            </StepLabel>

            <StepContent>
              Once all parties have signed and the deposit is confirmed,
              contracts can be formally exchanged.
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      <Alert severity="info">
        <b>What to do now: </b> Stay available and watch for notifications from
        your agent. You'll be asked to review and sign the Contract of Sale once
        it's prepared.
      </Alert>

      <Typography
        variant="body2"
        sx={{
          fontFamily: 'poppins',
          color: 'text.secondary',
        }}
      >
        If you have any questions about the outcome or next steps, please
        contact your agent directly.
      </Typography>
    </Stack>
  );
};
const WinnerNominated = ({ userName }) => {
  return (
    <Stack spacing={3}>
      <Typography
        color="black"
        sx={{
          fontFamily: 'poppins',
          fontSize: '16px',
          fontWeight: 500,
        }}
      >
        Congratulations! Winner Nominated!
      </Typography>

      <Typography
        sx={{
          fontFamily: 'poppins',
          fontWeight: 600,
          fontSize: '24px',
        }}
      >
        A Winner Has Been Nominated — Here's What Happens Next
      </Typography>

      <Typography sx={{ fontFamily: 'poppins' }}>
        Your agent has nominated a winning buyer for your property. The sale
        becomes legally binding only at exchange of contracts.
      </Typography>

      <Box>
        <Typography fontWeight={600} sx={{ fontFamily: 'poppins' }}>
          Next Steps In The Process
        </Typography>

        <Stepper orientation="vertical">
          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Contract Preparation
            </StepLabel>

            <StepContent>
              Your agent is preparing the Contract of Sale. It will be issued to
              both you and the buyer for review.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Both Parties Sign
            </StepLabel>

            <StepContent>
              You and the buyer will each be asked to review and sign the
              contract via retreX. If there are multiple vendors on the
              property, all vendors must sign.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Deposit Confirmed
            </StepLabel>

            <StepContent>
              The buyer is required to pay the deposit into your agent's trust
              account. Your agent will confirm receipt.
            </StepContent>
          </Step>

          <Step active>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '16px !important',
                  fontFamily: 'poppins !important',
                },
              }}
            >
              Exchange Confirmed
            </StepLabel>

            <StepContent>
              Once signatures and deposit are in order, your agent will formally
              confirm exchange. Your property is then legally sold.
            </StepContent>
          </Step>
        </Stepper>
      </Box>

      <Alert severity="warning">
        After exchange, a standard 5-business-day cooling-off period applies
        unless the buyer's solicitor has provided a s66W certificate.
      </Alert>

      <Typography
        variant="body2"
        sx={{
          fontFamily: 'poppins',
          color: 'text.secondary',
        }}
      >
        If you have any questions about the contract or the process, speak with
        your solicitor or contact your agent.
      </Typography>
    </Stack>
  );
};

export default BannerMoreInfoDialog;
